// ─────────────────────────────────────────────────────────────────────
// Tracker de getCheckout locales por mesa.
//
// Problema que resuelve: el backend `GetCheckout` hace self-heal de
// drift y, si corrige algo, emite `ChangeMesa` por SignalR al grupo
// `restaurante-{id}`. El grupo incluye al EMISOR (el propio caller),
// así que el mismo dispositivo que llamó recibe el evento y vuelve a
// disparar getCheckout → bounce + petición duplicada.
//
// Pero `mesa/{userId}` SÍ es útil cuando viene de OTRO dispositivo
// (alguien comandó, movió productos, cobró). En ese caso queremos
// recargar para ver los cambios.
//
// Solución: cada vez que ESTE dispositivo llama getCheckout para una
// mesa, marcamos timestamp. El handler del SignalR ignora el evento
// si el getCheckout local para esa mesa es muy reciente (~2s).
// ─────────────────────────────────────────────────────────────────────

const localFetches = new Map(); // mesaId → timestamp ms

/**
 * Marca que ESTE dispositivo acaba de hacer un getCheckout para esa
 * mesa — el siguiente bounce SignalR `mesa/{userId}` dentro de la
 * ventana se ignorará. Llamar JUSTO antes o después de cada `axios.get`
 * a `data/getCheckout/...`.
 */
export function markLocalCheckoutFetch(mesaId) {
  if (!mesaId) return;
  localFetches.set(mesaId, Date.now());
}

/**
 * ¿Hubo un getCheckout local para esta mesa dentro de la ventana?
 * Si sí, el bounce SignalR es ruido y debe ignorarse.
 */
export function isRecentLocalFetch(mesaId, windowMs = 2000) {
  if (!mesaId) return false;
  const ts = localFetches.get(mesaId);
  if (!ts) return false;
  return Date.now() - ts < windowMs;
}

// Mutaciones que NO cambian la mesa (no provocan `mesa/{rid}`): marcarlas
// abría la ventana de eco y se descartaban cambios de OTROS dispositivos.
// El heartbeat del lease (cada 10 s) la mantenía abierta ~20 % del tiempo.
const NON_MESA_MUTATIONS = [
  /(^|\/)mesas\/lease(\/|$)/i, // heartbeat y DELETE mesas/lease/{id}
  /(^|\/)mesas\/[^/]+\/lease(\/|$|\?)/i, // pedir/forzar el lease
  /(^|\/)tpv-journal(\/|$)/i,
  /(^|\/)pedidos\/getOrders(\/|$|\?)/i, // lectura por POST
  /(^|\/)api-monitor(\/|$)/i,
  /(^|\/)connectivity(\/|$)/i,
  /(^|\/)authentication(\/|$)/i,
  /(^|\/)employee-?(active)?session/i,
];

/** ¿La mutación a `url` puede cambiar la mesa (y traer su eco por SignalR)? */
export function mutationTouchesMesa(url) {
  if (!url) return false;
  const path = String(url).replace(/^https?:\/\/[^/]+/i, "");
  return !NON_MESA_MUTATIONS.some((re) => re.test(path));
}

/**
 * Limpia entradas viejas — opcional, evita crecimiento ilimitado del
 * Map si se hacen miles de cobros sin recargar la pestaña.
 */
export function pruneLocalFetches(olderThanMs = 60_000) {
  const cutoff = Date.now() - olderThanMs;
  for (const [k, ts] of localFetches.entries()) {
    if (ts < cutoff) localFetches.delete(k);
  }
}

// ─────────────────────────────────────────────────────────────────────
// Tracker equivalente para dinerSplit — evita el rebote del broadcast
// `dinerSplit/{restauranteId}` al mismo dispositivo que originó la
// acción. El thunk `moverProductosComensal*` ya aplica optimismo local
// + tiene la respuesta HTTP fresca; el broadcast que llega segundos
// después NO aporta información nueva pero dispara SyncCart (~50-200ms
// de red) y sobrescribe cart local. Marcar el timestamp por sesión al
// hacer la acción, y saltar el listener si es reciente.
// ─────────────────────────────────────────────────────────────────────

const localDinerSplitActions = new Map();

export function markLocalDinerSplitAction(idSession) {
  if (!idSession) return;
  localDinerSplitActions.set(idSession, Date.now());
}

export function isRecentLocalDinerSplitAction(idSession, windowMs = 2000) {
  if (!idSession) return false;
  const ts = localDinerSplitActions.get(idSession);
  if (!ts) return false;
  return Date.now() - ts < windowMs;
}
